import React, { useEffect, useState } from 'react';
import { Button, Alert } from 'antd';
import { useLocation, useHistory } from 'react-router-dom';
import { prop, assoc, isEmpty, not, propOr } from 'ramda';
import { AUTH_LOGGED_OUT_ELSEWHERE_PARAM } from '../../_CONST';
import SignInMutation from '../../_graphql/mutations/SignInMutation';
import AccountLayout from '../../layout/account/AccountLayout';
import { emailIsValid, passwordIsValid } from '../../lib/validation';
import useKeyPressEnter from '../../hooks/useKeyPressEnter';
import useActionGuard from '../../hooks/useActionGuard';
import withUser from '../../withUser';
import { errorNotification } from '../../utils/messageMutation';
import EmailPasswordForm from './components/EmailPasswordForm';

const useSignIn = (userRefetch) => {
  const [values, setValues] = useState({
    email: '',
    password: ''
  });
  const { loading, run } = useActionGuard();

  const setValue = (k, v) => setValues(assoc(k, v));

  const fieldIsDefined = k => !isEmpty(prop(k, values));

  const allFieldsAreDefined = () => fieldIsDefined('email') && fieldIsDefined('password');

  const onSignIn = () => {
    run(`sign-in-${prop('email', values)}`, (done) => {
      SignInMutation(prop('email', values), prop('password', values), (ok, error) => {
        done(ok, error);
        if (!ok || error) {
          errorNotification('Adresse e-mail ou mot de passe incorrect ou invalide.', 'Veuillez corriger avant de réessayer.');
        } else {
          userRefetch();
        }
      });
    });
  };

  return {
    emailIsValid: emailIsValid(prop('email', values)),
    passwordIsValid: passwordIsValid(prop('password', values)),
    fieldIsDefined,
    allFieldsAreDefined,
    loading: propOr(false, `sign-in-${prop('email', values)}`, loading),
    setValue,
    onSignIn
  };
};

const SignIn = ({ userRefetch }) => {
  const {
    emailIsValid,
    passwordIsValid,
    loading,
    fieldIsDefined,
    allFieldsAreDefined,
    setValue,
    onSignIn
  } = useSignIn(userRefetch);

  const location = useLocation();
  const history = useHistory();

  const [loggedOutElsewhere, setLoggedOutElsewhere] = useState(
    () => new URLSearchParams(location.search).has(AUTH_LOGGED_OUT_ELSEWHERE_PARAM)
  );

  useEffect(() => {
    if (loggedOutElsewhere) history.replace(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = () => {
    setLoggedOutElsewhere(false);
    onSignIn();
  };

  useKeyPressEnter(handleSignIn, false, { emailIsValid, passwordIsValid });

  return (
    <AccountLayout>
      <h3>Connexion</h3>

      {loggedOutElsewhere && (
        <Alert
          type="info"
          showIcon
          message="Vous avez été déconnecté depuis un autre onglet."
          className="mb-4 w-300px"
        />
      )}

      <EmailPasswordForm
        setValue={setValue} fieldIsDefined={fieldIsDefined} emailIsValid={emailIsValid}
        passwordIsValid={passwordIsValid}
        isSignIn
      />

      <br/>

      <Button
        disabled={not(allFieldsAreDefined())}
        type="primary"
        size="large"
        loading={loading}
        onClick={handleSignIn}
      >
        Se connecter
      </Button>
    </AccountLayout>
  );
};

export default withUser(SignIn);
